// src/pages/RegisterLogin.jsx
import React, { useState, useEffect } from "react";
import { Auth } from "aws-amplify";
import axios from "axios";
import { useNavigate } from "react-router-dom";

// -------- axios 实例：后端 API 基地址 ----------
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "/",
});

// 登录后给 axios 加统一 Authorization 头
function setAuthHeader(token) {
  if (token) {
    api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common["Authorization"];
  }
}
// ------------------------------------------------

const RegisterLogin = () => {
  const [mode, setMode] = useState("register"); // "register" | "login"
  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    email: "",
    password1: "",
    password2: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const navigate = useNavigate();

  // 从 Cognito Hosted UI（Google/GitHub）跳回来时，URL 会带 ?code=...
  useEffect(() => {
    const hasCode = window.location.search.includes("code=");
    if (!hasCode) return;

    (async () => {
      try {
        setLoading(true);
        const session = await Auth.currentSession();
        const idToken = session.getIdToken().getJwtToken();
        localStorage.setItem("idToken", idToken);
        setAuthHeader(idToken);
        setMsg("Login successful (social account).");
        navigate("/compare");
      } catch (err) {
        console.error("Handle social login callback error:", err);
        setMsg(err.message || "Social login failed.");
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const toggleMode = () => {
    setMode((m) => (m === "register" ? "login" : "register"));
    setForm({
      full_name: "",
      phone: "",
      email: "",
      password1: "",
      password2: "",
      password: "",
    });
    setMsg("");
  };

  const onChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // ---------- 表单提交逻辑：注册 or 登录 ----------
  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMsg("");

    // --------------- 注册模式 ---------------
    if (mode === "register") {
      try {
        if (form.password1 !== form.password2) {
          setMsg("Passwords do not match.");
          return;
        }
        if (!form.email) {
          setMsg("Email is required.");
          return;
        }
        if (!form.phone) {
          setMsg("Phone number is required.");
          return;
        }

        // 构造一个「不是 email 格式」的 username（因为 pool 开了 email alias）
        const rawName = (form.full_name || "").trim();
        let username = rawName.replace(/\s+/g, "_");
        if (!username) {
          username = form.email.split("@")[0] || "user";
        }

        // Phone 转成 E.164
        let phone = form.phone.trim();
        phone = phone.replace(/\s+/g, "");
        if (!phone.startsWith("+")) {
          phone = "+1" + phone; // 先默认 +1
        }

        const attributes = {
          email: form.email,
          name: rawName || username,
          phone_number: phone,
        };

        // ====== 尝试注册 ======
        try {
          const res = await Auth.signUp({
            username,
            password: form.password1,
            attributes,
          });

          console.log("Cognito signup success:", res);

          // ✅ 关键：把 username + email 存起来，后面 /verify 要用到
          localStorage.setItem("pendingUsername", username);
          localStorage.setItem("pendingEmail", form.email);

          navigate(`/verify?email=${encodeURIComponent(form.email)}`);
          return;
        } catch (err) {
          console.error("SignUp error:", err);

          // ✅ 邮箱已存在（多半是没确认）
          if (
            err.code === "UsernameExistsException" ||
            err.code === "AliasExistsException"
          ) {
            try {
              // 对于开了 email alias 的 pool，resend 可以用 email 也可以用 username
              // 这里我们优先用 email（因为老用户的 username 可能不是现在这个）
              await Auth.resendSignUp(form.email);

              // 存 pending 信息（username 可能未知，就先只保存 email）
              localStorage.setItem("pendingUsername", username);
              localStorage.setItem("pendingEmail", form.email);

              navigate(`/verify?email=${encodeURIComponent(form.email)}`);
              setMsg(
                "This email is already registered but not confirmed. We have re-sent the verification code."
              );
              return;
            } catch (e2) {
              console.error("Resend error:", e2);
              setMsg(e2.message || "Failed to resend verification code.");
              return;
            }
          } else {
            setMsg(err.message || "Registration failed.");
            return;
          }
        }
      } finally {
        setLoading(false);
      }
    }

    // --------------- 登录模式 ---------------
    try {
      const loginRes = await Auth.signIn(form.email, form.password); // email 作为 alias 登录
      console.log("Cognito login success:", loginRes);

      const session = await Auth.currentSession();
      const idToken = session.getIdToken().getJwtToken();
      localStorage.setItem("idToken", idToken);
      setAuthHeader(idToken);

      setMsg("Login successful!");
      navigate("/compare");
    } catch (err) {
      console.error("Login error:", err);
      setMsg(err.message || "Login failed.");
    } finally {
      setLoading(false);
    }
  };
  // ------------------------------------------------

  // ---------- Social logins：Google / GitHub ----------
  const loginGoogle = () => {
    Auth.federatedSignIn({ provider: "Google" });
  };

  const loginGitHub = () => {
    Auth.federatedSignIn({ provider: "GitHub" });
  };
  // ---------------------------------------------------

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-white px-4 font-sans tracking-wide">
      <div className="max-w-md w-full bg-white rounded-3xl shadow-2xl shadow-slate-200/70 p-8 border border-slate-100">
        {/* 标题 */}
        <div className="mb-6 text-center">
          <h1 className="text-3xl font-extrabold text-blue-600 mb-2 tracking-tight">
            SPTLab Cloud
          </h1>
          <p className="text-gray-600 text-sm">
            {mode === "register"
              ? "Register a new account"
              : "Sign in to your account"}
          </p>
        </div>

        {/* 提示信息 */}
        {msg && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-300 text-amber-800 rounded-xl text-sm whitespace-pre-wrap">
            {msg}
          </div>
        )}

        {/* 表单 */}
        <form onSubmit={onSubmit} className="space-y-4">
          {mode === "register" && (
            <>
              <input
                type="text"
                name="full_name"
                placeholder="Full Name"
                value={form.full_name}
                onChange={onChange}
                className="w-full px-4 py-2 border border-slate-300 bg-slate-50 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 text-sm"
              />
              <input
                type="text"
                name="phone"
                placeholder="Phone (e.g. +1 765xxxxxxx)"
                value={form.phone}
                onChange={onChange}
                className="w-full px-4 py-2 border border-slate-300 bg-slate-50 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 text-sm"
                required
              />
            </>
          )}

          <input
            type="email"
            name="email"
            placeholder="Email"
            value={form.email}
            onChange={onChange}
            className="w-full px-4 py-2 border border-slate-300 bg-slate-50 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 text-sm"
            required
          />

          {mode === "register" ? (
            <>
              <input
                type="password"
                name="password1"
                placeholder="Password"
                value={form.password1}
                onChange={onChange}
                className="w-full px-4 py-2 border border-slate-300 bg-slate-50 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 text-sm"
                required
              />
              <input
                type="password"
                name="password2"
                placeholder="Confirm Password"
                value={form.password2}
                onChange={onChange}
                className="w-full px-4 py-2 border border-slate-300 bg-slate-50 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 text-sm"
                required
              />
            </>
          ) : (
            <input
              type="password"
              name="password"
              placeholder="Password"
              value={form.password}
              onChange={onChange}
              className="w-full px-4 py-2 border border-slate-300 bg-slate-50 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 text-sm"
              required
            />
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-2.5 rounded-full text-sm font-semibold hover:bg-blue-700 transition shadow-md shadow-blue-200 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading
              ? "Processing..."
              : mode === "register"
              ? "Register"
              : "Login"}
          </button>
        </form>

        {/* 切换登录/注册 */}
        <div className="mt-4 text-center">
          <button
            onClick={toggleMode}
            className="text-sm text-blue-600 hover:underline"
          >
            {mode === "register"
              ? "Already have an account? Login"
              : "Don't have an account? Register"}
          </button>
        </div>

        {/* 分割线 */}
        <div className="my-6 flex items-center">
          <hr className="flex-grow border-gray-200" />
          <span className="mx-3 text-gray-400 text-xs uppercase tracking-[0.2em]">
            or continue with
          </span>
          <hr className="flex-grow border-gray-200" />
        </div>

        {/* Social logins */}
        <div className="space-y-3">
          {/* Google：黑白风格 */}
          <button
            type="button"
            onClick={loginGoogle}
            className="w-full flex items-center justify-center gap-3 bg-white border border-gray-300 text-gray-700 py-2.5 rounded-full text-sm font-medium shadow-sm hover:shadow-md hover:bg-gray-50 transition"
          >
            <img
              src="https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/google.svg"
              alt="Google"
              className="w-4 h-4"
            />
            <span>Continue with Google</span>
          </button>

          {/* GitHub：黑色 */}
          <button
            type="button"
            onClick={loginGitHub}
            className="w-full flex items-center justify-center gap-3 bg-black text-white py-2.5 rounded-full text-sm font-medium shadow-sm hover:shadow-lg hover:bg-zinc-900 transition"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-4 h-4"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M12 0a12 12 0 00-3.8 23.4c.6.1.8-.3.8-.6v-2.1c-3.3.7-4-1.6-4-1.6-.5-1.2-1.2-1.6-1.2-1.6-1-.7.1-.7.1-.7 1.1.1 1.7 1.2 1.7 1.2 1 .1 2.5-.6 3.1-1 .1-.7.4-1.1.7-1.4-2.6-.3-5.3-1.3-5.3-5.7 0-1.3.5-2.4 1.2-3.2 0-.3-.5-1.5.1-3.1 0 0 1-.3 3.4 1.3a11.6 11.6 0 016.2 0c2.4-1.6 3.4-1.3 3.4-1.3.6 1.6.1 2.8.1 3.1.8.8 1.2 1.9 1.2 3.2 0 4.5-2.7 5.4-5.3 5.7.4.3.7 1 .7 2v3c0 .3.2.7.8.6A12 12 0 0012 0z" />
            </svg>
            <span>Continue with GitHub</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default RegisterLogin;
